// -----------------------------------------------------------------------------
// Movies.tsx — handles BOTH single‑movie reviews and “Top 10” listicles
// -----------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row       from "react-bootstrap/Row";
import Col       from "react-bootstrap/Col";
import Card      from "react-bootstrap/Card";
import Form      from "react-bootstrap/Form";

import { movie_details } from "../../assets/movies/movie_details";     // ← path to your data array
import { Post, ReviewPost, Top10Post } from "../../data/types";       // ← path to your interfaces
import twitter from "../../assets/images/twitter.png";
import { FacebookIcon, FacebookShareButton } from "react-share";
import bluesky from "../../assets/images/bluesky.svg";

// -----------------------------------------------------------
// local helpers & internal types
// -----------------------------------------------------------
type LoadedImages = Record<string, string>;

interface MoviesState {
    post: Post | null;
    posts: Post[];
    allPosts: boolean;
    loadedImages: LoadedImages;
    shareUrl: string;
}

// -----------------------------------------------------------
// React component
// -----------------------------------------------------------
const Movies: React.FC = () => {
    const { movie = "all" } = useParams<{ movie: string }>();

    const [state, setState] = useState<MoviesState>({
        post: null,
        posts: [],
        allPosts: true,
        loadedImages: {},
        shareUrl: "",
    });

    // ---------------------------------------------------------
    // side‑effects on slug change
    // ---------------------------------------------------------
    useEffect(() => {
        clearStorage();

        if (movie === "all") {
            // list view
            setState((s) => ({ ...s, allPosts: true }));
            preloadAllCovers(movie_details);
        } else {
            // single post view
            const found = movie_details.find((p) => p.movie === movie);
            if (found) {
                setState((s) => ({
                    ...s,
                    post: found,
                    allPosts: false,
                    shareUrl: `https://twitter.com/intent/tweet?text=Check%20out%20this%20post%20on%20https://noadshere.com/movies/${movie}`,
                }));
                preloadImages(found);
            }
        }

        // list always needed for search field
        setState((s) => ({ ...s, posts: movie_details }));
    }, [movie]);

    // ---------------------------------------------------------
    // helpers
    // ---------------------------------------------------------
    const clearStorage = (): void => {
        document.cookie
            .split(";")
            .forEach((c) => {
                document.cookie = c
                    .replace(/^ +/, "")
                    .replace(/=.*/, "=;expires=Thu, 01 Jan 1970 00:00:00 GMT");
            });
        localStorage.clear();
        sessionStorage.clear();
    };

    const preloadImages = (post: Post): void => {
        // single review
        if (post.kind === "review") {
            (["image_1", "image_2", "image_3", "image_4", "image_5", "image_6"] as const)
                .forEach((key) => {
                    const img = (post as ReviewPost)[key] as ReviewPost["image_1"];
                    if (img?.image) {
                        import(`../../assets/images/${img.image}`).then((mod) => {
                            setState((s) => ({
                                ...s,
                                loadedImages: { ...s.loadedImages, [key]: mod.default },
                            }));
                        });
                    }
                });
        }

        // listicle — grab every item image
        if (post.kind === "top10") {

            const top = post as Top10Post;

            /* hero / card art */
                if (top.cover?.image) {
                    import(`../../assets/images/${top.cover.image}`).then((mod) => {
                        setState((s) => ({
                                ...s,
                               loadedImages: { ...s.loadedImages, cover: mod.default },
                        }));
                    });
                }

            (post as Top10Post).items.forEach((item) => {
                if (item.image?.image) {
                    import(`../../assets/images/${item.image.image}`).then((mod) => {
                        setState((s) => ({
                            ...s,
                            loadedImages: {
                                ...s.loadedImages,
                                [`${post.movie}-${item.rank}`]: mod.default,
                            },
                        }));
                    });
                }
            });
        }
    };

    const preloadAllCovers = (posts: Post[]): void => {
        posts.forEach((p) => {
            let ref: { image?: string } | undefined;

            if (p.kind === "review") ref = (p as ReviewPost).image_1;
            //if (p.kind === "top10")  ref = (p as Top10Post).items[0]?.image;
            if (p.kind === "top10")  ref = (p as Top10Post).cover;

            if (ref?.image) {
                import(`../../assets/images/${ref.image}`).then((mod) => {
                    setState((s) => ({
                        ...s,
                        loadedImages: { ...s.loadedImages, [p.movie]: mod.default },
                    }));
                });
            }
        });
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const q = e.target.value.toLowerCase();
        const filtered = movie_details.filter(({ movie: slug }) =>
            slug.includes(q)
        );
        setState((s) => ({ ...s, posts: filtered }));
    };

    const contentFlow: (keyof ReviewPost)[] = [
        "paragraph_1",
        "paragraph_2",
        "image_2",
        "paragraph_3",
        "image_3",
        "paragraph_4",
        "paragraph_5",
        "paragraph_6",
        "image_4",
        "paragraph_7"
    ];


    // ---------------------------------------------------------
    // render helpers
    // ---------------------------------------------------------
    const renderReview = (post: ReviewPost) => (
        <Col sm={12} lg={8}>
            <div className="movieDiv">
                {/* header */}
                <h1 className="darkGrey">
                    {post.name}
                    {post.subtitle && <p className="subTitle">{post.subtitle}</p>}
                    <span className="movieDate">
            {post.date} –{" "}
                        {post.reviewer.twitter ? (
                            <a href={post.reviewer.twitter} target="_blank" rel="noopener noreferrer">
                                {post.reviewer.name}
                            </a>
                        ) : (
                            post.reviewer.name
                        )}
          </span>
                </h1>

                {/* main image */}
                {state.loadedImages.image_1 && (
                    <>
                        <img
                            className="movieImage"
                            src={state.loadedImages.image_1}
                            alt={post.image_1?.text}
                        />
                        <figcaption className="fig">
                            <h4>{post.image_1?.text}</h4>
                        </figcaption>
                    </>
                )}

                {contentFlow.map((key) => {
                    if (key.startsWith("paragraph")) {
                        const content = post[key];
                        if (typeof content === "string" || typeof content === "function") {
                            return (
                                <p key={key} className="movieBlurb">
                                    {typeof content === "function" ? content() : content}
                                </p>
                            );
                        }
                        return null;
                    }
                    if (key.startsWith("image")) {
                        const ref = post[key] as { image?: string; text?: string };
                        const src = state.loadedImages[key as string];
                        return ref?.image && src ? (
                            <div key={key}>
                                <br />
                                <img className="movieImage" src={src} alt={ref.text} />
                                <figcaption className="fig">
                                    <h4>{ref.text}</h4>
                                </figcaption>
                                <br />
                            </div>
                        ) : null;
                    }
                    return null;
                })}



                {/* metadata summary */}
                {post.summary && (
                    <p className="movieBlurb">
                        <strong>Summary: </strong>
                        {post.summary}
                    </p>
                )}
                <p className="movieBlurb">
                    <strong>Rating:</strong> {post.rating} stars
                </p>

                <p className="movieBlurb endBit sources">
                    All images courtesy of:{" "}
                    <a href={post.image_sources} target="_blank" rel="noopener noreferrer">
                        {post.image_sources}
                    </a>
                </p>
            </div>
        </Col>
    );

    const renderDiversityBlock = (post: ReviewPost) => (
        <Col className="diversityCol" sm={12} lg={4}>
            <div className="diversityDiv">
                <p className="leftText movieBlurb">
                    <strong>Diverse or not diverse?</strong>
                </p>
                {(post.diversity ?? []).map((item) => (
                    <Card className={item.className} key={item.question}>
                        <p className="movieBlurb">
                            <strong>{item.question}</strong>
                        </p>
                        <p className="movieBlurb">{item.answer}</p>
                    </Card>
                ))}
            </div>

            {/* share buttons */}
            <h3 className="shareText">
                <strong>Share this post on:</strong>
            </h3>
            <a
                className="twitter-share-button"
                href={state.shareUrl}
                target="_blank"
                rel="noopener noreferrer"
            >
                <img className="social floatLeft" src={twitter} alt="Twitter logo" />
            </a>
            <br />
            <br />
            <FacebookShareButton
                className="facebookButton"
                url={`https://www.noadshere.com/movies/${post.movie}`}
                hashtag="#NOadsHERE"
            >
                <FacebookIcon size={32} round />
            </FacebookShareButton>
            <br />
            <br />
        </Col>
    );

    /* ---------------------------------------------------------- */
    /* helper — returns null if it can’t find a real YouTube ID   */
    /* ---------------------------------------------------------- */
    const toYoutubeEmbed = (idOrUrl?: string): string | null => {
        if (!idOrUrl) return null;

        // plain 11‑char ID
        if (/^[\w-]{11}$/.test(idOrUrl)) {
            return `https://www.youtube.com/embed/${idOrUrl}`;
        }

        try {
            const u = new URL(idOrUrl);
            // youtu.be/<id>
            if (u.hostname.includes("youtu.be")) {
                const id = u.pathname.slice(1); // remove leading /
                return id ? `https://www.youtube.com/embed/${id}` : null;
            }
            // youtube.com/watch?v=<id>
            if (u.hostname.includes("youtube.com")) {
                const id = u.searchParams.get("v");
                return id ? `https://www.youtube.com/embed/${id}` : null;
            }
        } catch (_) {
            /* fall through */
        }

        return null; // not a recognised YouTube link
    };

    /* ---------------------------------------------------------- */
    /*  create share links                                         */
    /* ---------------------------------------------------------- */
    const shareUrlFor = (slug: string) =>
        `https://www.noadshere.com/movies/${slug}`;

    const twitterShare = (slug: string) =>
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            "Check out this post on NOadsHERE: " + shareUrlFor(slug)
        )}`;

    const blueskyShare = (slug: string) =>
        `https://bsky.app/intent/compose?text=${encodeURIComponent(
            "Check out this post on NOadsHERE: " + shareUrlFor(slug)
        )}`;


    const ShareBar: React.FC<{ slug: string }> = ({ slug }) => (
        <div>

            <h3 className="shareText">
                <strong>Share this post on:</strong>
            </h3>

            <div className="shareButtons">
                {/* ─────────── Twitter ─────────── */}
                <a
                    href={twitterShare(slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shareBtn"
                    aria-label="Share on Twitter"
                >
                    <img src={twitter} alt="" />
                </a>

                {/* ─────────── Bluesky ─────────── */}
                <a
                    href={blueskyShare(slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shareBtn"
                    aria-label="Share on Bluesky"
                >
                    <img src={bluesky} alt="" />
                </a>

                {/* ─────────── Facebook ─────────── */}
                <FacebookShareButton
                    url={shareUrlFor(slug)}
                    hashtag="#NOadsHERE"
                    className="shareBtn"
                    aria-label="Share on Facebook"
                >
                    <FacebookIcon size={32} round />
                </FacebookShareButton>
            </div>

            <br />
            <br />
        </div>
    );


    /* ------------------------------------------------------------------ */
    /*  Vertical Top‑10 article layout with optional YouTube embed        */
    /* ------------------------------------------------------------------ */
    const renderTop10 = (post: Top10Post) => (
        <Col sm={12}>
            <div className="movieDiv top10Article">
                {/* header ---------------------------------------------------- */}
                <h1 className="top10-post-title">
                    {post.name}
                    <br/>
                    <span className="movieDate">
                        {post.date}
                        {post.reviewer && " – "}
                        {post.reviewer?.twitter ? (
                            <a
                                href={post.reviewer.twitter}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {post.reviewer.name}
                            </a>
                        ) : (
                            post.reviewer?.name
                        )}
                    </span>
                </h1>

                {state.loadedImages.cover && (
                    <img
                        className="movieImage mb-3"
                        src={state.loadedImages.cover}
                        alt={post.cover.alt || post.name}
                        style={{ maxHeight: 450, objectFit: "cover" }}
                       />
                )}

                {post.coverSummary && (
                    <p className="movieBlurb">{post.coverSummary}</p>
                )}

                {/* body ------------------------------------------------------ */}
                {post.items
                    .sort((a, b) => b.rank - a.rank)
                    .map((item) => {
                        const key   = `${post.movie}-${item.rank}`;
                        const img   = state.loadedImages[key];
                        const video = toYoutubeEmbed(item.youtubeId ?? item.youtubeUrl);

                        return (
                            <article key={key} className="mb-5">
                                <h2 className="rankHeading">
                                    {item.rank}. {item.title}
                                </h2>

                                <p className="movieBlurb">{item.blurb}</p>

                                {img && (
                                    <>
                                        <img
                                            className="movieImage mb-1"
                                            src={img}
                                            alt={item.image?.alt || item.title}
                                            style={{ maxHeight: 400, objectFit: "cover" }}
                                        />
                                        {item.imageSource && (
                                            <p
                                                className="imageCredit"
                                            >
                                                Image source: {item.imageSource}
                                            </p>
                                        )}
                                    </>
                                )}


                                {video && (
                                    <div className="videoWrapper my-3">
                                        <iframe
                                            width="100%"
                                            height="315"
                                            src={video}
                                            title={item.title}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        />
                                    </div>
                                )}

                                {item.releaseDate && (
                                    <p className="movieBlurb">
                                        {img && <br/>}
                                        <strong className="text-muted">
                                            Release Date: </strong>{item.releaseDate}
                                    </p>
                                )}
                            </article>
                        );
                    })}
                <ShareBar slug={post.movie} />
            </div>
        </Col>
    );


    const cardBackground = (p: Post): string | undefined => {
        if (p.kind === "review") return state.loadedImages[p.movie];
        if (p.kind === "top10") return state.loadedImages[p.movie];
    };

    // ---------------------------------------------------------
    // JSX return
    // ---------------------------------------------------------
    return (
        <div className="divmor">
            <Container>
                <Row className="movieRow">
                    {/* ----------------- SINGLE POST ----------------- */}
                    {!state.allPosts && state.post && (
                        <>
                            {state.post.kind === "review" && renderReview(state.post)}
                            {state.post.kind === "review" && renderDiversityBlock(state.post)}
                            {state.post.kind === "top10" && renderTop10(state.post)}
                        </>
                    )}

                    {/* ----------------- LIST / ARCHIVE -------------- */}
                    {state.allPosts && (
                        <Col sm={12}>
                            <div className="movieDiv">
                                <Row>
                                    <Col sm={12} lg={8}>
                                        <h1 className="darkGrey">Movie Posts</h1>
                                        <h4>
                                            Search all our single‑movie reviews and curated Top 10
                                            lists in one place.
                                        </h4>
                                        <br />
                                    </Col>
                                </Row>

                                {/* search box */}
                                <Row className="mb-3">
                                    <Col sm={12} lg={4}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search movies"
                                            onChange={handleSearch}
                                        />
                                    </Col>
                                </Row>

                                {/* grid of cards */}
                                <Row className="allRow">
                                    {state.posts.map((p) => (
                                        <Col sm={4} lg={4} key={p.movie} className="tvCol">
                                            <div
                                                className="bgImageDiv"
                                                style={{
                                                    backgroundImage: cardBackground(p)
                                                        ? `url(${cardBackground(p)})`
                                                        : "none",
                                                    backgroundSize: "cover",
                                                }}
                                            >
                                                <Card
                                                    className={`contentCard tv ${p.kind === "top10" ? "top10" : ""}`}
                                                >
                                                    <a className="cardLink" href={`/movies/${p.movie}`}>
                                                        <p className="cardLinkText">{p.name}</p>
                                                    </a>
                                                </Card>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        </Col>
                    )}
                </Row>
            </Container>

            {/* footer */}
            <div className="footerAbout">
                <a className="footerLinks" href="/">
                    home
                </a>
                <a className="footerLinks" href="/tv/all">
                    tv
                </a>
                <a className="footerLinks" href="/movies/all">
                    movies
                </a>
                <a className="footerLinks" href="/recipes/all">
                    recipes
                </a>
                <a className="footerLinks" href="/about/">
                    about
                </a>
            </div>
        </div>
    );
};

export default Movies;
